import store from '@/core/store'
import NotificationsService from '@/app-domains/user/session/notifications/NotificationService'
import { setMe, setMainContent, setMeField, setSignature } from '@/core/store/Slices/MeSlice'
import { getCustomerBillingData, getMeInfo, updateMeSignature, requestPurchaseAnotherDomainAPICall } from '@/core/http/me.api'
import boxesService from '@/app-domains/boxes/boxes.service'
import labelsService from '@/app-domains/labels/labels.service'
import commonsService from '@/services/commons.service'
import activitiesService from '@/app-domains/activity/activity.service'
import signUpService from '@/app-domains/user/onboarding/signup.service'
import ContainersService from '@/services/containers.service'
import threadsService from '@/app-domains/threads/threads.service'
import { waitUntil } from 'async-wait-until'
import DraftManagerService from '@/app-domains/messages/draft/service/DraftManager.service'
import { removeItemFromLocalStorage, saveItemInLocalStorage } from '@/core/localstorage'
import contactslabelsService from '@/app-domains/contactslabels/contactslabels.service'
import contactsService from '@/app-domains/contacts/contacts.service'

class SessionService {
  async initSession () {
    let meStatus
    this.sendAppLoadingSignal(true)
    this.setMainContentLoaded(false)

    await this.setMeInfo().then(async () => {
      meStatus = this.getUserStatus()

      if (this.isUserInOnboardingState(meStatus)) {
        await this.handleSessionInitializationDuringOnboarding(this.getMe().me)
        return meStatus
      }
      if (this.isUserInCancelledState(meStatus)) {
        this.handleSessionInitializationForCancelledUser()
        return meStatus
      }

      this.setInitialData().then(
        () => {
          setTimeout(() => this.setNotifications(), 1000)
          store.commit('APP:is_loading', false)
        }
      )
      return meStatus
    })

    return meStatus
  }

  setMainContentLoaded (value) {
    store.dispatch(setMainContent({ loaded: value }))
  }

  sendAppLoadingSignal (value) {
    store.commit('APP:is_loading', value)
  }

  isUserInOnboardingState (status) {
    return status === 'onboarding' || status === 'byod_pending'
  }

  isUserInCancelledState (status) {
    return status === 'subscription-cancel' || status === 'trial_cancelled'
  }
  
  async handleSessionInitializationDuringOnboarding (me) {
    if (!this.isUserOnWaitingPageForOnboarding()) {
      this.sendAppLoadingSignal(false)
      return signUpService.returnToSignUpProcess(me)
    }
    try {
      await this.waitUntilUserHasActiveSubscriptions()
    } catch (e) {
      signUpService.returnToSignUpProcess(me)
    }
  }

  handleSessionInitializationForCancelledUser () {
    commonsService.goToPage('/settings/billing?reactivate_subscription=true')
    this.sendAppLoadingSignal(false)
    setTimeout(() => store.commit('REACTIVATE_SUBSCRIPTION_MODAL:SHOW'), 200)
  }

  isUserOnWaitingPageForOnboarding () {
    return window.location.pathname.includes('/waiting-page') || window.location.pathname.includes('/byod-setup-step')
  }

  async waitUntilUserHasActiveSubscriptions () {
    await waitUntil(async () => {
      await this.setMeInfo()
      const meStatus = store.getState().me.me
      return signUpService.hasActiveSubscriptions(meStatus)
    }, {
      timeout: 240000,
      intervalBetweenAttempts: 20000,
    })
  }

  getMe () {
    return store.getState().me
  }

  async updateSignature (signature) {
    await updateMeSignature({ signature })
    store.dispatch(setSignature({ signature }))
  }

  isUserLogged () {
    return this.getMe().logged
  }

  getUserStatus () {
    return this.getMe().me.status
  }

  async setNotifications () {
    NotificationsService.setPlayNotificationFn(commonsService.play)
    NotificationsService.setProvider()
    NotificationsService.subscribe()
  }

  async setMeInfo () {
    const res = await getMeInfo()
    if (res.status === 200) {
      store.dispatch(setMe(res.data))
      store.commit('ME:info_updated')
    } else if (res.status === 401) {
      commonsService.removeStorageAndRefresh()
    }
  }

  async setBillingInfo () {
    try {
      const data = await getCustomerBillingData()
      store.dispatch(setMeField({ field: 'billing', value: data.billingData }))
    } catch (e) {
      // eslint-disable-next-line
      console.log(e)
    }
  }

  setPostOnboardingTemporalData (data) {
    saveItemInLocalStorage('onboardingData', data)
  }

  removePostOnboardingTemporalData (data) {
    removeItemFromLocalStorage('onboardingData', data)
  }

  async setInitialData () {
    await Promise.all([
      boxesService.getUserBoxes(),
      labelsService.getLabels(),
      activitiesService.getActivities(),
      threadsService.queryLastThreads(),
      DraftManagerService.getLastDrafts(),
      contactslabelsService.getAllContactLabels(),
      contactsService.getAllContacts()
    ])

    if (this.getMe().me.customerid !== 'non-billed' && this.getUserStatus() !== 'onboarding' && this.getUserStatus() !== 'byod_pending') { this.setBillingInfo() }

    ContainersService.generateContainersMap()
    store.dispatch(setMainContent({ loaded: true }))
  }

  async requestPurchaseAnotherDomain (domain) {
    await requestPurchaseAnotherDomainAPICall(domain)
    this.setMeInfo()
  }
  
  isSandboxSession () {
    return this.getMe().me.isSandbox
  }
  
  hasUnlabeledVisible () {
    return this.getMe().me.preferences?.isUnlabeledShown
  }
}

export default new SessionService()
